import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, Button, Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/src/context/AuthContext';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import { Logo } from '@/src/components/Logo';

type Phase = 'working' | 'error' | 'existing-account';

/** Screens an access link may return to (fixed allowlist; mirrors the backend). */
const ACCESS_RETURN_ROUTES: Record<string, string> = { partner: '/partner', 'partner-apply': '/partner-apply' };

/**
 * Partner email-possession verification (D-057 dedicated minimal registration).
 *
 * The single-use token arrives in the URL FRAGMENT on web (`/partner-verify#token=`)
 * so it is never sent to the server as a request line/referrer, and as a query
 * param on the native deep link. The token is read once, immediately scrubbed
 * from the address bar, never logged/rendered. A successful verification returns
 * the auth key for the (possibly newly created, bare) canonical account; the
 * application remains PENDING. Verification never self-approves.
 *
 * A non-secret `next` (query) chooses where an ACCESS link returns, from a fixed
 * allowlist only; anything else falls back to the application screen.
 */
export default function PartnerVerify() {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const router = useRouter();
  const { authenticateWithToken } = useAuth();
  const params = useLocalSearchParams<{ token?: string; next?: string }>();
  const [phase, setPhase] = useState<Phase>('working');
  // What the existing-account notice must say (registration into an existing account).
  const [notice, setNotice] = useState({ passwordUnchanged: false, accountNifReused: false });
  const ran = useRef(false);

  // Read before the token scrub below rewrites the address bar.
  const readNext = useCallback((): string => {
    let next = typeof params.next === 'string' ? params.next : '';
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // The router may have already folded the query behind the fragment
      // (`#token=…?next=partner`), so look for it anywhere in the address.
      const match = /[?&]next=([A-Za-z-]+)/.exec(window.location.href || '');
      if (match) next = match[1];
    }
    // Own keys only: `constructor`, `__proto__`… must not resolve to a route.
    return Object.prototype.hasOwnProperty.call(ACCESS_RETURN_ROUTES, next) ? ACCESS_RETURN_ROUTES[next] : '/partner-apply';
  }, [params.next]);

  const readToken = useCallback((): string => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const hash = window.location.hash || '';
      const match = /(?:^#|&)token=([A-Za-z0-9_\-]+)/.exec(hash);
      // Scrub the secret from the address bar immediately, before any await.
      try { window.history.replaceState(null, '', window.location.pathname); } catch { /* noop */ }
      if (match) return match[1];
    }
    return typeof params.token === 'string' ? params.token : '';
  }, [params.token]);

  // Late responses must not authenticate or navigate once the screen is gone.
  const mounted = useRef(true);
  // Re-arm on (StrictMode) remount: `ran` keeps the single-use call from re-running,
  // so the surviving instance must still be allowed to complete it.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const accessReturn = readNext();
    const token = readToken();
    if (!token) { setPhase('error'); return; }
    void (async () => {
      // `ambiguous` = transport failure (status 0 / thrown) or a 5xx: the server
      // may have consumed the single-use token, so the outcome is UNKNOWN and
      // the token must not be replayed against another endpoint.
      type Consumed =
        | { kind: 'ok'; key: string; email?: string; passwordUnchanged: boolean; accountNifReused: boolean }
        | { kind: 'rejected' }
        | { kind: 'ambiguous' };
      const consume = async (endpoint: string): Promise<Consumed> => {
        try {
          const response = await apiFetch<{ key?: string; email?: string; password_unchanged?: boolean; account_nif_reused?: boolean }>(
            `${API_BASE}${endpoint}`,
            {
              method: 'POST',
              includeCompanyContext: false,
              singleAttempt: true,
              body: JSON.stringify({ token }),
            }, false, false,
          );
          if (response.status === 0 || response.status >= 500) return { kind: 'ambiguous' };
          if (response.ok && response.data?.key) {
            return {
              kind: 'ok', key: response.data.key, email: response.data.email || undefined,
              passwordUnchanged: response.data.password_unchanged === true,
              accountNifReused: response.data.account_nif_reused === true,
            };
          }
          return { kind: 'rejected' };
        } catch { return { kind: 'ambiguous' }; }
      };
      try {
        // A registration-intent token materializes the pending application; an
        // access token (R35) signs an existing partner back in. Try both so the
        // single fixed /partner-verify route serves enrollment and return — but
        // only fall through on a DEFINITIVE rejection of the first.
        let result = await consume('/api/referrals/partner-registration/verify/');
        let destination = '/partner-apply';
        if (!mounted.current) return;
        if (result.kind === 'rejected') {
          result = await consume('/api/referrals/partner-access/verify/');
          destination = accessReturn;
        }
        if (!mounted.current) return;
        if (result.kind === 'ok') {
          // Passing the account email clears the previous account's local
          // owner/driver storage (cross-account fencing).
          await authenticateWithToken(result.key, result.email);
          if (!mounted.current) return;
          // The email already had an account: say so before moving on, since
          // the password typed in the form was deliberately not applied.
          if (result.passwordUnchanged || result.accountNifReused) {
            setNotice({ passwordUnchanged: result.passwordUnchanged, accountNifReused: result.accountNifReused });
            setPhase('existing-account');
            return;
          }
          router.replace(destination as never);
          return;
        }
        // Rejected or ambiguous: both show the error with the fresh-link
        // recovery; an ambiguous outcome is never silently retried.
        setPhase('error');
      } catch { setPhase('error'); }
    })();
  }, [readToken, authenticateWithToken, router]);

  return <ScrollView style={{ backgroundColor: theme.colors.background }} contentContainerStyle={{ padding: 16, gap: 16, maxWidth: 640, width: '100%', alignSelf: 'center' }}>
    <View testID="partner-brand" accessible accessibilityLabel="AllDrivers"><Logo size="medium" /></View>
    <Text variant="headlineMedium">{t('verifyTitle')}</Text>
    <Card style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12 }}>
      {phase === 'working' ? <>
        <ActivityIndicator />
        <Text testID="verify-working">{t('verifyWorking')}</Text>
      </> : phase === 'existing-account' ? <>
        {notice.passwordUnchanged && <Text accessibilityRole="alert" testID="verify-existing-account">{t('verifyExistingAccount')}</Text>}
        {notice.accountNifReused && <Text accessibilityRole="alert" testID="verify-account-nif-reused">{t('verifyAccountNifReused')}</Text>}
        <Button mode="contained" contentStyle={{ minHeight: 44 }} testID="verify-existing-continue"
          onPress={() => router.replace('/partner-apply' as never)}>{t('verifyExistingContinue')}</Button>
      </> : <>
        <Text accessibilityRole="alert" testID="verify-error">{t('verifyError')}</Text>
        <Button mode="contained" contentStyle={{ minHeight: 44 }} testID="verify-retry"
          onPress={() => router.replace('/partner-apply' as never)}>{t('verifyContinue')}</Button>
      </>}
    </Card.Content></Card>
  </ScrollView>;
}
